import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Translate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../api';
import { getLanguageDisplayName } from '../product-details/utils';

interface BulkTranslateDialogProps {
  open: boolean;
  selectedCount: number;
  onClose: () => void;
  onConfirm: (targetLanguage: string) => void;
  loading: boolean;
}

export const BulkTranslateDialog: React.FC<BulkTranslateDialogProps> = ({
  open,
  selectedCount,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();
  const api = useApi();
  const [targetLanguage, setTargetLanguage] = useState('');
  const [availableTargets, setAvailableTargets] = useState<string[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingLanguages(true);
    api
      .get('/glossaries/targets?source=en')
      .then((data: unknown) => {
        const raw = Array.isArray(data) ? data : [];
        const targets = raw
          .map((c: unknown) => String(c).toLowerCase())
          .filter((l: string) => l && l !== 'en')
          .sort();
        setAvailableTargets(targets);
        if (targets.length > 0 && !targetLanguage) {
          setTargetLanguage(targets[0]);
        }
      })
      .catch(() => {
        setAvailableTargets([]);
      })
      .finally(() => {
        setLoadingLanguages(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = () => {
    if (targetLanguage) {
      onConfirm(targetLanguage);
    }
  };

  const targetDisplayName = targetLanguage ? getLanguageDisplayName(targetLanguage) : '';

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Translate color="primary" />
        {t('products.bulk.translateDialog.title', 'Bulk Translate')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2.5}>
          {t(
            'products.bulk.translateDialog.description',
            'Translate {{count}} selected product(s). Products that already have a translation in the target language will be skipped.',
            { count: selectedCount }
          )}
        </Typography>

        {loadingLanguages ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>{t('products.bulk.translateDialog.targetLanguage', 'Target language')}</InputLabel>
            <Select
              value={targetLanguage}
              label={t('products.bulk.translateDialog.targetLanguage', 'Target language')}
              onChange={(e) => setTargetLanguage(e.target.value)}
              disabled={loading}
            >
              {availableTargets.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {getLanguageDisplayName(lang)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {targetLanguage && !loadingLanguages && (
          <Alert severity="info" sx={{ mt: 2 }} icon={false}>
            <Typography variant="body2">
              {t(
                'products.bulk.translateDialog.creditInfo',
                'Uses up to {{count}} translation credit(s) to translate to {{language}}.',
                { count: selectedCount, language: targetDisplayName }
              )}
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!targetLanguage || loading || loadingLanguages}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Translate />}
        >
          {loading
            ? t('products.bulk.translateDialog.translating', 'Translating…')
            : t('products.bulk.translateDialog.confirm', 'Translate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
